import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  AttachMoney,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import RevenueService from '../../services/revenueService';
import ExpenseService from '../../services/expenseService';
import AccountsReceivableService from '../../services/accountsReceivableService';
import AccountingDataFetchService from '../../services/accountingDataFetchService';
import StaffSalaryService from '../../services/staffSalaryService';
import { getCurrencySymbolFromSchool } from '../../utils/currencyFormatter';

const FinancialDashboard = () => {
  const schooldetails = useSelector(state => state.schooldetails || []);
  const currentSchool = schooldetails.length > 0 ? schooldetails[0] : null;
  const schoolID = currentSchool?.schoolID;
  
  // Get currency symbol from school details
  const currencySymbol = getCurrencySymbolFromSchool(schooldetails);

  // Local state for fetched data
  const [students, setStudents] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [sales, setSales] = useState([]);
  const [classData, setClassData] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  
  // Use refs to track previous values and prevent unnecessary recalculations
  const prevDataRef = useRef({});
  const isCalculatingRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Fetch data from database
  const fetchDataFromDatabase = async () => {
    if (!schoolID || isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      const data = await AccountingDataFetchService.fetchAllAccountingData(schoolID);
      
      setStudents(data.students || []);
      setClassData(data.classes || []);
      setExpenses(data.expenses || []);
      setSales(data.sales || []);
      setTeachers(data.teachers || []);

      isFetchingRef.current = false;
    } catch (err) {
      console.error('Error fetching data from database:', err);
      setError('Failed to fetch data from database');
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when schoolID changes
  useEffect(() => {
    if (schoolID) {
      fetchDataFromDatabase();
    }
  }, [schoolID]);

  // Memoize date calculations to prevent unnecessary recalculations
  // Match the same logic used in School Fees tab (accounting.js handlePeriodChange)
  // Use simple date creation - the date formatting in revenueService.js handles payment date format conversion
  const dateRange = useMemo(() => {
    const today = new Date();
    let start, end;

    switch (selectedPeriod) {
      case 'current':
        // Simple date creation - same as School Fees tab
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case 'quarter':
        // Simple date creation - same as School Fees tab
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        start = quarterStart;
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case 'year':
        // Simple date creation - same as School Fees tab
        start = new Date(today.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        // Simple date creation - same as School Fees tab
        start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  }, [selectedPeriod, startDate, endDate]);

  // Create stable data keys for comparison
  // Include dateRange start/end times directly to ensure period changes trigger recalculation
  const dataKey = useMemo(() => {
    const startTime = dateRange.start.getTime();
    const endTime = dateRange.end.getTime();
    return JSON.stringify({
      studentsCount: students?.length || 0,
      expensesCount: expenses?.length || 0,
      salesCount: sales?.length || 0,
      classCount: classData?.length || 0,
      teachersCount: teachers?.length || 0,
      period: selectedPeriod,
      startTime: startTime,
      endTime: endTime
    });
  }, [students?.length, expenses?.length, sales?.length, classData?.length, teachers?.length, selectedPeriod, dateRange.start.getTime(), dateRange.end.getTime()]);

  useEffect(() => {
    // Prevent infinite loops by checking if data actually changed
    if (isCalculatingRef.current) return;
    
    const currentDataKey = dataKey;
    if (prevDataRef.current.key === currentDataKey) {
      return; // Data hasn't changed, skip calculation
    }

    const calculateMetrics = () => {
      if (isCalculatingRef.current) return;
      isCalculatingRef.current = true;

      try {
        setLoading(true);
        setError(null);

        // Get fresh dateRange to ensure we use the latest period selection
        const { start, end } = dateRange;
        
        console.log('Recalculating metrics with period:', selectedPeriod, 'dateRange:', {
          start: start.toISOString(),
          end: end.toISOString(),
          startTime: start.getTime(),
          endTime: end.getTime()
        });

        // Get mainterm (current term) - matching school fees page logic
        // Matching: schooldetails_[0].terms[schooldetails_[0].terms.length-1] (line 680 in tableschoolfees.js)
        const mainterm = schooldetails && schooldetails.length > 0 && 
                         schooldetails[0].terms && 
                         Array.isArray(schooldetails[0].terms) && 
                         schooldetails[0].terms.length > 0
          ? schooldetails[0].terms[schooldetails[0].terms.length - 1]
          : "none";

        const revenue = RevenueService.calculateTotalRevenue(
          students, sales, classData, start, end, mainterm
        );

        // Debug logging for school fees calculation
        console.log('Revenue Calculation Debug:', {
          studentsCount: students.length,
          studentsWithFinance: students.filter(s => s.finance && s.finance.length > 0).length,
          schoolFeesRevenue: revenue.schoolFees,
          schoolFeesAllTime: revenue.breakdown.schoolFees?.totalPaymentsAllTime || 0, // All payments (no date filter)
          revenueBreakdown: revenue.breakdown.schoolFees,
          mainterm: mainterm,
          maintermType: typeof mainterm,
          schooldetailsTerms: schooldetails && schooldetails.length > 0 && schooldetails[0].terms 
            ? schooldetails[0].terms 
            : 'no terms',
          dateRange: { 
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0],
            startTime: start.getTime(),
            endTime: end.getTime(),
            selectedPeriod: selectedPeriod
          },
          note: 'schoolFeesAllTime should match the school fees page total. If schoolFeesRevenue is 0 but schoolFeesAllTime > 0, date filtering is the issue.'
        });

        // Calculate staff salaries from payment records
        console.log('Calculating staff salaries for', teachers.length, 'teachers');
        console.log('Date range:', { start, end });
        const staffSalaries = StaffSalaryService.calculateStaffSalaries(teachers, start, end);
        console.log('Staff salaries result:', staffSalaries);

        const expense = ExpenseService.getExpenseBreakdown(
          expenses, [], start, end  // Pass empty array, we add salaries separately
        );

        // Add staff salaries to expenses
        const totalExpenses = expense.total + staffSalaries.total;
        
        // Update expense breakdown to include salaries
        if (!expense.byCategory['Salaries & Wages']) {
          expense.byCategory['Salaries & Wages'] = 0;
        }
        expense.byCategory['Salaries & Wages'] = staffSalaries.total;

        const accountsReceivable = AccountsReceivableService.getAccountsReceivableSummary(
          students, classData
        );

        const netIncome = revenue.total - totalExpenses;
        const profitMargin = revenue.total > 0
          ? ((netIncome / revenue.total) * 100)
          : 0;

        setMetrics({
          totalRevenue: revenue.total,
          totalDiscounts: revenue.breakdown?.schoolFees?.discountsGiven || 0,
          totalExpenses: totalExpenses,  // Updated to include salaries
          netIncome,
          profitMargin,
          revenue,
          expense: {
            ...expense,
            total: totalExpenses,
            staffSalaries: staffSalaries  // Add salary details
          },
          accountsReceivable,
          period: { start, end }
        });

        prevDataRef.current.key = currentDataKey;
        setLoading(false);
      } catch (err) {
        console.error('Error calculating metrics:', err);
        setError('Failed to calculate financial metrics');
        setLoading(false);
      } finally {
        isCalculatingRef.current = false;
      }
    };

    // Only calculate if we have data
    if (students.length > 0 || expenses.length > 0 || sales.length > 0) {
      calculateMetrics();
    } else if (!isFetchingRef.current && schoolID) {
      // If no data and not currently fetching, try to fetch
      fetchDataFromDatabase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setSelectedPeriod(newPeriod);
    
    // Match the same logic as School Fees tab (accounting.js handlePeriodChange)
    const today = new Date();
    switch (newPeriod) {
      case 'current':
        setStartDate(new Date(today.getFullYear(), today.getMonth(), 1));
        setEndDate(today);
        break;
      case 'quarter':
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        setStartDate(quarterStart);
        setEndDate(today);
        break;
      case 'year':
        setStartDate(new Date(today.getFullYear(), 0, 1));
        setEndDate(today);
        break;
      default:
        // For 'custom', keep existing dates
        break;
    }
    
    // Force recalculation by clearing previous data key
    // This ensures the useEffect will trigger even if dataKey comparison might fail
    prevDataRef.current.key = null;
    
    console.log('Period changed to:', newPeriod, 'dateRange will update');
  };

  if (!schoolID) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No school selected. Please select a school to view accounting data.
      </Alert>
    );
  }

  if (loading && !metrics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchDataFromDatabase}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          No financial data available. Click "Refresh Data" to load data from the database.
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchDataFromDatabase}
        >
          Load Data
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Print Styles for Staff Salaries */}
      <style>
        {`
          @media print {
            .staff-salaries-section {
              background-color: #fff3cd !important;
              border: 2px solid #ffc107 !important;
              page-break-inside: avoid;
            }
            .staff-salaries-card {
              border: 2px solid #ffc107 !important;
            }
          }
        `}
      </style>

      {/* Period Selection */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              label="Period"
            >
              <MenuItem value="current">Current Month</MenuItem>
              <MenuItem value="quarter">Current Quarter</MenuItem>
              <MenuItem value="year">Current Year</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDataFromDatabase}
            disabled={loading || !schoolID}
            size="small"
          >
            Refresh Data
          </Button>

          {selectedPeriod === 'custom' && (
            <>
              <TextField
                label="Start Date"
                type="date"
                value={startDate.toISOString().split('T')[0]}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate.toISOString().split('T')[0]}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </>
          )}
        </Box>
        
        {/* Display selected period range */}
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Showing data from: <strong>{dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</strong> - <strong>{dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>
        </Typography>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Revenue
                  </Typography>
                  <Typography variant="h5" color="primary">
                    {currencySymbol} {metrics.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Expenses
                  </Typography>
                  <Typography variant="h5" color="error">
                    {currencySymbol} {metrics.totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <TrendingDown sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Net Income
                  </Typography>
                  <Typography
                    variant="h5"
                    color={metrics.netIncome >= 0 ? 'success.main' : 'error.main'}
                  >
                    {currencySymbol} {metrics.netIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <AccountBalance sx={{ fontSize: 40, color: metrics.netIncome >= 0 ? 'success.main' : 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Profit Margin
                  </Typography>
                  <Typography
                    variant="h5"
                    color={metrics.profitMargin >= 0 ? 'success.main' : 'error.main'}
                  >
                    {metrics.profitMargin.toFixed(2)}%
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: metrics.profitMargin >= 0 ? 'success.main' : 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Discounts Given
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {currencySymbol} {(metrics.totalDiscounts || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <TrendingDown sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Staff Salaries Card - Featured */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: '2px solid', borderColor: 'warning.main' }} className="staff-salaries-card">
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Staff Salaries
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {currencySymbol} {(metrics.expense.staffSalaries?.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {metrics.expense.staffSalaries?.staffCount || 0} staff
                  </Typography>
                  {metrics.expense.staffSalaries?.totalBonuses > 0 && (
                    <Typography variant="caption" color="success.main" display="block">
                      Bonuses: {currencySymbol} {(metrics.expense.staffSalaries.totalBonuses || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  )}
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Breakdown */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Revenue Breakdown
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ 
                px: 1.5, 
                py: 0.5, 
                bgcolor: 'primary.light', 
                borderRadius: 1,
                fontWeight: 'medium'
              }}>
                Period: {dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
            {!metrics.revenue && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                No revenue data available for this period.
              </Typography>
            )}
            {metrics.revenue && (
              <Box sx={{ mt: 2 }}>
                {(() => {
                  const totalRev = metrics.revenue?.total || 0;
                  const schoolFees = metrics.revenue?.schoolFees || 0;
                  const sales = metrics.revenue?.sales || 0;
                  const canteen = metrics.revenue?.canteen || 0;
                  const otherIncome = metrics.revenue?.otherIncome || 0;
                  const discountsGiven = metrics.revenue?.breakdown?.schoolFees?.discountsGiven || 0;
                  
                  return (
                    <>
                      <Box display="flex" justifyContent="space-between" mb={1.5} p={1} sx={{ bgcolor: 'primary.light', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">School Fees:</Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold" color="primary.dark">
                            {currencySymbol} {schoolFees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          {totalRev > 0 && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {((schoolFees / totalRev) * 100).toFixed(1)}% of total
                            </Typography>
                          )}
                          {/* Show all-time total for comparison if available */}
                          {metrics.revenue?.breakdown?.schoolFees?.totalPaymentsAllTime && 
                           metrics.revenue.breakdown.schoolFees.totalPaymentsAllTime > schoolFees && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                              All-time: {currencySymbol} {(metrics.revenue.breakdown.schoolFees.totalPaymentsAllTime || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Sales:</Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {currencySymbol} {sales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          {totalRev > 0 && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {((sales / totalRev) * 100).toFixed(1)}%
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Canteen:</Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {currencySymbol} {canteen.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          {totalRev > 0 && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {((canteen / totalRev) * 100).toFixed(1)}%
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Other Income:</Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {currencySymbol} {otherIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          {totalRev > 0 && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {((otherIncome / totalRev) * 100).toFixed(1)}%
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="warning.main">Discounts Given:</Typography>
                        <Typography variant="body2" fontWeight="bold" color="warning.main">
                          {currencySymbol} {discountsGiven.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      {/* Show total revenue for reference */}
                      <Box display="flex" justifyContent="space-between" sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body1" fontWeight="bold">Total Revenue:</Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary">
                          {currencySymbol} {totalRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </>
                  );
                })()}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Expense Breakdown
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1} p={1} sx={{ bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">Staff Salaries:</Typography>
                <Typography variant="body2" fontWeight="bold" color="warning.dark">
                  {currencySymbol} {(metrics.expense.staffSalaries?.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Other Expenses:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {currencySymbol} {(metrics.totalExpenses - (metrics.expense.staffSalaries?.total || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" fontWeight="bold">Total Expenses:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {currencySymbol} {metrics.totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2">Accounts Receivable:</Typography>
                <Typography variant="body2" fontWeight="bold" color="error">
                  {currencySymbol} {metrics.accountsReceivable.totalOutstanding.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Number of Debtors:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {metrics.accountsReceivable.debtorCount}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Expense Breakdown */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Expense Breakdown by Category
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Show Salaries & Wages first and prominently */}
          {metrics.expense.byCategory['Salaries & Wages'] !== undefined && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" p={2} sx={{ bgcolor: 'warning.light', borderRadius: 1, mb: 1 }} className="staff-salaries-section">
                <Box>
                  <Typography variant="body1" fontWeight="bold">Salaries & Wages</Typography>
                  {metrics.expense.staffSalaries && (
                    <Typography variant="caption" color="textSecondary">
                      {metrics.expense.staffSalaries.staffCount} staff members
                      {metrics.expense.staffSalaries.totalBonuses > 0 && ` • Bonuses: ${currencySymbol} ${(metrics.expense.staffSalaries.totalBonuses || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </Typography>
                  )}
                </Box>
                <Typography variant="h6" fontWeight="bold" color="warning.dark">
                  {currencySymbol} {(metrics.expense.byCategory['Salaries & Wages'] || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* Then show other categories */}
          {Object.entries(metrics.expense.byCategory)
            .filter(([category, amount]) => category !== 'Salaries & Wages' && amount > 0)
            .map(([category, amount]) => (
              <Grid item xs={12} sm={6} md={4} key={category}>
                <Box display="flex" justifyContent="space-between" p={1} sx={{ bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2">{category}:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {currencySymbol} {amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Grid>
            ))}
        </Grid>
      </Paper>

      {/* Staff Salaries Breakdown - Always show if staffSalaries exists */}
      {metrics.expense.staffSalaries && (
        <Paper sx={{ p: 2, mt: 3, bgcolor: metrics.expense.staffSalaries.total > 0 ? 'warning.light' : 'grey.100' }} className="staff-salaries-section">
          <Typography variant="h6" gutterBottom>
            Staff Salaries Breakdown
          </Typography>
          {metrics.expense.staffSalaries.total > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" justifyContent="space-between" p={1} sx={{ bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2">Total Salaries:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {currencySymbol} {metrics.expense.staffSalaries.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" justifyContent="space-between" p={1} sx={{ bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2">Base Salaries:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {currencySymbol} {metrics.expense.staffSalaries.totalBase.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" justifyContent="space-between" p={1} sx={{ bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2">Bonuses:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {currencySymbol} {metrics.expense.staffSalaries.totalBonuses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" justifyContent="space-between" p={1} sx={{ bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2">Staff Count:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {metrics.expense.staffSalaries.staffCount}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Breakdown by Position */}
              {Object.keys(metrics.expense.staffSalaries.byPosition).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    By Position:
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    {Object.entries(metrics.expense.staffSalaries.byPosition)
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([position, data]) => (
                        <Grid item xs={12} sm={6} md={4} key={position}>
                          <Box display="flex" justifyContent="space-between" p={1} sx={{ bgcolor: 'white', borderRadius: 1 }}>
                            <Typography variant="body2">{position} ({data.count}):</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {currencySymbol} {data.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No salary payments found in the selected period. Staff salaries will appear here when payments are recorded.
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default FinancialDashboard;
